import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context';
import portalService from '../../services/portalService';
import PortalShell from './PortalShell';

/**
 * /portal/apply — the two-question form.
 *
 * Per spec mock Portal_Apply.png: the Portal Page snapshot at the top serves
 * as the "we read every word" confirmation, then two free-text fields with
 * exact placeholders + helpers from the ticket.
 */
const INVITE_STORAGE_KEY = 'portal:inviteCode';

export default function PortalApplyPage() {
  const navigate = useNavigate();
  const { profile, isLoading: userLoading } = useUser();
  const [referrer, setReferrer] = useState('');
  const [why, setWhy] = useState('');
  const [remarkable, setRemarkable] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [inviteCode, setInviteCode] = useState(null);
  const [invitePreview, setInvitePreview] = useState(null);

  // Read the stashed invite code, pre-fill the referrer with the inviter's
  // name so the applicant doesn't have to retype it (and so Reza sees the
  // chain of trust clearly).
  useEffect(() => {
    let code = null;
    try { code = sessionStorage.getItem(INVITE_STORAGE_KEY); } catch {}
    if (!code) return;
    setInviteCode(code);
    portalService.previewPortalInvite(code).then((r) => {
      if (r.success && r.data) {
        setInvitePreview(r.data);
        if (!r.data.expired && !r.data.used && !r.data.revoked) {
          setReferrer((current) => current || r.data.inviterDisplayName);
        }
      }
    });
  }, []);

  useEffect(() => {
    // Hop out if user already has a non-eligible state.
    portalService.getState().then((r) => {
      if (!r.success) return;
      if (r.data?.state === 'accepted') navigate('/noir/home', { replace: true });
      if (r.data?.state === 'pending') navigate('/noir', { replace: true });
      if (r.data?.state === 'declined' || r.data?.state === 'not_yet') navigate('/noir', { replace: true });
      if (r.data?.state === 'no_profile') navigate('/onboard', { replace: true });
      if (r.data?.state === 'anonymous') navigate('/login?redirect=/portal/apply', { replace: true });
    });
  }, [navigate]);

  if (userLoading) {
    return <PortalShell><div style={{ padding: 80, textAlign: 'center' }}>Loading…</div></PortalShell>;
  }

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!referrer.trim() || !why.trim() || !remarkable.trim()) {
      setError('All fields are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const pitchText = `Why do you want to be part of this network?\n${why.trim()}\n\nWhat do you think makes you remarkable?\n${remarkable.trim()}`;
    const r = await portalService.submitApplication({ referrerText: referrer, pitchText, inviteCode });
    setSubmitting(false);
    if (r.success) {
      // Consume the invite code — only valid for one application.
      try { sessionStorage.removeItem(INVITE_STORAGE_KEY); } catch {}
      navigate('/noir/thanks', { replace: true });
    } else {
      setError(r.error || 'Could not submit application');
    }
  };

  return (
    <PortalShell>
      <div style={{ maxWidth: 720, margin: '60px auto 80px', padding: '0 24px' }}>
        <button
          type="button"
          onClick={() => navigate('/noir')}
          className="portal-mono"
          style={{ background: 'none', border: 'none', color: 'rgba(236,231,218,0.34)', cursor: 'pointer', fontSize: 11, marginBottom: 24 }}
        >
          ← BACK
        </button>

        <p className="portal-label" style={{ marginBottom: 14 }}>REQUEST A SEAT</p>
        <h1 className="portal-hero" style={{ marginBottom: 24 }}>Two questions.</h1>
        {invitePreview && !invitePreview.expired && !invitePreview.used && !invitePreview.revoked && (
          <div className="portal-card" style={{ padding: 14, marginBottom: 24, borderColor: '#C9A24B' }}>
            <p className="portal-mono portal-gold" style={{ fontSize: 10, marginBottom: 4 }}>VOUCHED FOR BY</p>
            <p className="portal-serif" style={{ fontStyle: 'italic', color: '#ECE7DA' }}>
              <strong>{invitePreview.inviterDisplayName}</strong>
              {invitePreview.inviterHandle ? <span className="portal-mono" style={{ fontSize: 10, color: 'rgba(236,231,218,0.34)', marginLeft: 6 }}>@{invitePreview.inviterHandle}</span> : null}
            </p>
          </div>
        )}
        <p className="portal-prose" style={{ marginBottom: 40 }}>
          Your Portal Page is most of the application.
        </p>

        {/* Portal snapshot */}
        <div className="portal-card" style={{ padding: 18, display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
          <div className="portal-avatar" style={{ width: 56, height: 56 }}>
            {profile?.avatarUrl || profile?.faceUrl ? (
              <img src={profile.avatarUrl || profile.faceUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : null}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="portal-mono" style={{ fontSize: 13, marginBottom: 4 }}>{profile?.displayName || ''}</div>
            <div className="portal-mono portal-gold" style={{ fontSize: 10 }}>
              PORTAL PAGE PULLED ✓
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 28 }}>
            <label className="portal-label" style={{ display: 'block', marginBottom: 8 }}>
              WHO TOLD YOU ABOUT PORTAL NOIR
            </label>
            <input
              type="text"
              className="portal-input"
              placeholder="A name. A handle. A story."
              value={referrer}
              onChange={(e) => setReferrer(e.target.value)}
              maxLength={2000}
              disabled={submitting}
            />
          </div>

          <div style={{ marginBottom: 28 }}>
            <label className="portal-serif" style={{ display: 'block', marginBottom: 8, fontSize: 18, color: '#ECE7DA' }}>
              Why do you want to be part of this network?
            </label>
            <textarea
              className="portal-input"
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              maxLength={4000}
              disabled={submitting}
            />
          </div>

          <div style={{ marginBottom: 28 }}>
            <label className="portal-serif" style={{ display: 'block', marginBottom: 8, fontSize: 18, color: '#ECE7DA' }}>
              What do you think makes you remarkable?
            </label>
            <p className="portal-whisper" style={{ marginTop: 0, marginBottom: 10 }}>
              Remarkable looks like many different things. A network is strong because of the variety of people in it, and how they interact.
            </p>
            <textarea
              className="portal-input"
              value={remarkable}
              onChange={(e) => setRemarkable(e.target.value)}
              maxLength={4000}
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="portal-mono" style={{ color: '#B4736A', fontSize: 11, marginBottom: 16 }}>{error}</p>
          )}

          <button type="submit" className="portal-btn solid" disabled={submitting}>
            {submitting ? 'SENDING…' : 'SUBMIT'}
          </button>
        </form>
      </div>
    </PortalShell>
  );
}
